import { Link } from 'react-router-dom';
import { BannerCurso } from './BannerCurso';
import type { ResumenCurso } from '../domain/types';

/**
 * Una tarjeta dice en qué estado está ESE curso para ESA persona: bloqueado, sin empezar, a
 * medias con su porcentaje, o aprobado con su nota. Un catálogo que se ve igual para todos
 * no le dice a nadie qué le toca hacer.
 */
export function TarjetaCurso({ resumen }: { resumen: ResumenCurso }) {
  const { curso, mejorNota, aprobado, porcentajeAvance } = resumen;
  const bloqueado = curso.estado === 'proximamente';
  const empezado = porcentajeAvance > 0;

  // Un curso bloqueado todavía no tiene lecciones: la tarjeta promete lo previsto.
  const lecciones = curso.lecciones.length || curso.leccionesPrevistas || 0;
  const preguntas = curso.preguntas.length || curso.preguntasPrevistas || 0;

  const etiqueta = aprobado
    ? `APROBADO · NOTA ${mejorNota}`
    : mejorNota !== null
      ? `ÚLTIMA NOTA ${mejorNota} · ¡REINTENTA!`
      : curso.etiqueta;

  return (
    <article
      className={`flex flex-col overflow-hidden rounded-2xl border border-borde bg-white shadow-[0_10px_30px_rgba(30,63,148,.08)] ${
        bloqueado ? 'opacity-75' : ''
      }`}
    >
      <BannerCurso curso={curso}>
        {aprobado && (
          <span className="absolute right-3 top-3 rounded-full bg-verde px-3 py-1 text-[0.62rem] font-extrabold text-white">
            ✓ COMPLETADO
          </span>
        )}
        {bloqueado && (
          <span className="absolute right-3 top-3 rounded-full bg-black/45 px-3 py-1 text-[0.62rem] font-extrabold text-white">
            🔒 PRÓXIMAMENTE
          </span>
        )}
      </BannerCurso>

      <div className="flex flex-1 flex-col p-5">
        <p className="text-[0.62rem] font-extrabold uppercase tracking-widest text-gris">
          {etiqueta}
        </p>
        <h3 className="mt-1 font-titulos text-lg font-extrabold leading-tight">{curso.titulo}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-gris">{curso.descripcion}</p>

        <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs font-bold text-gris">
          <span>⏱ {curso.duracionMin} min</span>
          <span>📖 {lecciones} lecciones</span>
          <span>📝 {preguntas} preguntas</span>
        </p>

        {!bloqueado && !aprobado && empezado && (
          <div className="mt-4">
            <div className="mb-1 flex justify-between text-xs font-extrabold text-gris">
              <span>Tu avance</span>
              <span>{porcentajeAvance}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-cielo">
              <div
                className="h-full rounded-full bg-azul transition-all"
                style={{ width: `${porcentajeAvance}%` }}
              />
            </div>
          </div>
        )}

        {bloqueado ? (
          <p className="mt-5 rounded-full bg-cielo py-3 text-center text-sm font-extrabold text-gris">
            Disponible próximamente
          </p>
        ) : (
          <Link
            to={`/curso/${curso.id}`}
            className={`mt-5 block rounded-full py-3 text-center text-sm font-extrabold transition ${
              aprobado
                ? 'border-[1.5px] border-borde text-gris hover:bg-cielo'
                : 'bg-rojo text-white hover:bg-rojo-osc'
            }`}
          >
            {aprobado ? 'Repasar curso' : empezado ? 'Continuar →' : 'Comenzar curso →'}
          </Link>
        )}
      </div>
    </article>
  );
}
